import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { EMPTY_MESSAGES, ROUTES } from '@/shared/lib/constants';
import { BadgeList } from '@/shared/ui/BadgeList';

import type { TagItem } from '@/entities/tags';
import { useAvailableTags, useTagsTreeSelect } from '@/entities/tags/model';

import { useUpdateAssetTags } from '@/features/asset-detail/model/useAssetTags';

type UseAssetTagsSelectParams = {
  entityType: string;
  entityId: string;
  fqn: string;
  classificationTags: TagItem[];
  classificationTagsDisplay: TagItem[];
};

export const useAssetTagsSelect = ({
  entityType,
  entityId,
  fqn,
  classificationTags,
  classificationTagsDisplay,
}: UseAssetTagsSelectParams) => {
  const navigate = useNavigate();
  const { data: availableTags = [] } = useAvailableTags();
  const { tagsTreeData, tagsValues, isLoading: isLoadingTree } = useTagsTreeSelect(classificationTags);
  const { mutateAsync: updateTags, isPending: isUpdating } = useUpdateAssetTags();

  const existingTagFQNs = useMemo(() => new Set(classificationTags.map((t) => t.tagFQN)), [classificationTags]);

  const handleSaveTags = async (newTagFQNs: string[]) => {
    await updateTags({
      entityType,
      entityId,
      fqn,
      newTagFQNs,
      availableTags,
    });
  };

  const onBadgeClick = useCallback(
    (tagFQN: string) => {
      navigate(`${ROUTES.TAGS_DETAIL}/${encodeURIComponent(tagFQN)}`);
    },
    [navigate]
  );

  const renderTagBadgeList = useCallback(() => {
    return (
      <BadgeList
        isEllipsis={false}
        items={classificationTagsDisplay}
        onItemClick={onBadgeClick}
        existingValues={existingTagFQNs}
        emptyText={EMPTY_MESSAGES.NO_CONNECTED_TAGS}
      />
    );
  }, [classificationTagsDisplay, onBadgeClick, existingTagFQNs]);

  const [isOpenEditTagModal, setIsOpenEditTagModal] = useState(false);
  const tagTreeSelectValue = useMemo(() => {
    if (!classificationTagsDisplay) return [];
    return classificationTagsDisplay.map((tag) => ({ value: tag.tagFQN, label: tag.displayName ?? tag.name }));
  }, [classificationTagsDisplay]);

  return {
    tagsTreeData,
    tagsValues,
    handleSaveTags,
    renderTagBadgeList,
    isLoading: isLoadingTree,
    isUpdating,
    isOpenEditTagModal,
    setIsOpenEditTagModal,
    tagTreeSelectValue,
  };
};
